import { Router, ActivatedRoute } from '@angular/router';
import { Component, OnInit, Input, OnChanges, Output, EventEmitter } from '@angular/core';
import { ElementModel } from 'projects/core/src/lib/models/element.model';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { ExercisepartModel } from 'projects/core/src/lib/models/exercisepart.model';
import {
  faStreetView,
  faUsers,
  faUserTie,
} from '@fortawesome/pro-light-svg-icons';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { TranslocoService } from '@ngneat/transloco';
import { ExerciseModel } from 'projects/core/src/lib/models/exercise.model';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';

@Component({
  selector: 'app-element-visibility',
  templateUrl: './element-visibility.component.html',
  styleUrls: ['./element-visibility.component.scss']
})
export class ElementVisibilityComponent implements OnInit, OnChanges {

  @Input() exercise: ExerciseModel = null;
  @Input() element: ElementModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public inEdit = false;
  public submitting = false;
  public form: UntypedFormGroup;
  public permissions: Array<any> = [];
  public inheritVisible = false;

  // icons
  public faStreetView = faStreetView;
  public faUsers = faUsers;
  public faUserTie = faUserTie;

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private auth: AuthStateService,
    private route: ActivatedRoute,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      permissions: this.fb.array([])
    });
    this.ui.setFocus();
  }

  ngOnChanges(): void {
    this.buildPermissions();
  }

  onEdit(): void {
    if (this.auth.hasRight('admin.element.edit')) {
      this.startEdit();
    }
  }

  onCancel(): void {
    this.stopEdit();
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      if (this.exercise?.shouldShowInheritDialog()) {
        this.inheritVisible = true;
      } else {
        this.savePermissions(false);
      }
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  public savePermissions(inherit: boolean = false): void {
    this.submitting = true;
    const payload = {
      permissions: [],
      inherit
    };
    for (const control of (this.form.get('permissions') as UntypedFormArray).controls) {
      if (control.get('moderator').value === true) {
        payload.permissions.push({
          id: control.get('id').value,
          moderator: true,
          candidate: control.get('candidate').value,
          observer: control.get('observer').value,
        });
      }
    }
    this.api.saveElementPermissions(this.element.id, payload).subscribe({
      next: (result: any) => {
        this.ui.toastSuccess(this.transloco.translate('element_visibility.visibility_saved'), this.transloco.translate('common.saved'));
        if (this.element.id !== result.data.id) {
          this.route.params.subscribe(params => {
            localStorage.setItem('element-tab-' + result.data.id, '2'); // save selected tab
            this.router.navigate(['/exercises', this.exercise.id, 'elements', result.data.id], { replaceUrl: true });
          });
        } else {
          this.element.permissions = [];
          if (result.data?.permissions) {
            this.element.permissions = result.data.permissions;
          }
          this.buildPermissions();
        }
        this.stopEdit();
      },
      error: (error: any) => {
        this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
        FormHelper.setBackendErrors(this.form, error.fielderrors);
      }
    }).add(() => {
      this.submitting = false;
    });
}

  startEdit(): void {
    this.buildForm();
    this.inEdit = true;
    this.setEdit.emit('visibility');
    this.ui.setFormFocus();
    this.setFormFields();
  }

  stopEdit(): void {
    this.clearForm();
    this.inEdit = false;
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  public setFormFields(): void {
    for (const control of (this.form.get('permissions') as UntypedFormArray).controls) {
      if (control.get('moderator').value === true) {
        control.get('candidate').enable();
        control.get('observer').enable();
        if (control.get('candidate').value === true || control.get('observer').value === true) {
          control.get('moderator').disable();
        } else {
          control.get('moderator').enable();
        }
      } else {
        control.get('candidate').disable();
        control.get('observer').disable();
        control.get('candidate').setValue(false);
        control.get('observer').setValue(false);
      }
    }
  }

  private buildPermissions(): void {
    this.permissions = [];
    if (this.element?.exercise?.exerciseparts) {
      this.element.exercise.exerciseparts.forEach((exercisepart: ExercisepartModel) => {
        const elementValue = this.element.permissions.find((permission: any) => permission.id === exercisepart.id);
        this.permissions.push({
          id: exercisepart.id,
          name: exercisepart.name,
          moderator: (elementValue !== undefined) ? true : false,
          candidate: (elementValue !== undefined && elementValue.candidate === true) ? true : false,
          observer: (elementValue !== undefined && elementValue.observer === true) ? true : false,
        });
      });
    }
  }

  private buildForm(): void {
    this.clearForm();
    const entries = this.form.get('permissions') as UntypedFormArray;
    if (this.element?.exercise?.exerciseparts) {
      this.element.exercise.exerciseparts.forEach((exercisepart: ExercisepartModel) => {
        const elementValue = this.element.permissions.find((permission: any) => permission.id === exercisepart.id);
        const entry = this.fb.group({
          id: new UntypedFormControl(exercisepart.id),
          name: new UntypedFormControl(exercisepart.name),
          moderator: new UntypedFormControl((elementValue !== undefined) ? true : false),
          candidate: new UntypedFormControl((elementValue !== undefined && elementValue.candidate === true) ? true : false),
          observer: new UntypedFormControl((elementValue !== undefined && elementValue.observer === true) ? true : false),
        });
        entries.push(entry);
      });
    }
  }

  private clearForm(): void {
    this.form.reset();
    const entries = this.form.get('permissions') as UntypedFormArray;
    while (entries.length > 0) {
      entries.removeAt(0);
    }
  }

}
